/**
 * App — Layout principal Synapse
 * Sidebar complet (13 items du mockup) + Header (search + sync + bell + profile) + routing
 */

import { Routes, Route, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import Dashboard from "./pages/Dashboard";
import MissionControl from "./pages/MissionControl";
import Projects from "./pages/Projects";
import Crew from "./pages/Crew";
import Settings from "./pages/Settings";
import Logs from "./pages/Logs";
import Schedules from "./pages/Schedules";
import DRP from "./pages/DRP";
import Cicd from "./pages/Cicd";
import Cluster from "./pages/Cluster";
import Resources from "./pages/Resources";
import NotificationsBell from "./components/NotificationsBell";
import GlobalSearch from "./components/GlobalSearch";
import SyncButton from "./components/SyncButton";
import UserProfile from "./components/UserProfile";
import ToastContainer from "./components/Toast";
import { api } from "./lib/api";

interface NavItem {
  to: string;
  icon: string;
  label: string;
  badge?: string;
}

const NAV_SECTIONS: { label: string; items: NavItem[] }[] = [
  {
    label: "Commandement",
    items: [
      { to: "/", icon: "🛰", label: "Mission Control" },
      { to: "/new-idea", icon: "💡", label: "Nouvelle idée" },
      { to: "/kanban", icon: "📋", label: "Kanban" },
      { to: "/projects", icon: "📦", label: "Projets" },
    ],
  },
  {
    label: "Vaisseau",
    items: [
      { to: "/crew", icon: "👥", label: "Équipage" },
      { to: "/logs", icon: "📡", label: "Ship Logs" },
    ],
  },
  {
    label: "Observabilité",
    items: [
      { to: "/cluster", icon: "🛡", label: "Cluster" },
    ],
  },
  {
    label: "Livraison",
    items: [
      { to: "/cicd", icon: "🚀", label: "CI/CD & Déploiements" },
      { to: "/resources", icon: "💰", label: "Ressources" },
      { to: "/schedules", icon: "⏰", label: "Missions programmées" },
      { to: "/drp", icon: "🆘", label: "Plan de reprise" },
    ],
  },
  {
    label: "Configuration",
    items: [
      { to: "/settings", icon: "⚙", label: "Settings" },
    ],
  },
];

interface ChannelStatus {
  label: string;
  sublabel?: string;
  dotClass: string;
  connected: boolean;
}

export default function App() {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [projectCount, setProjectCount] = useState<number>(0);
  const [channels, setChannels] = useState<ChannelStatus[]>([
    { label: "Discord", sublabel: "connecté", dotClass: "discord", connected: true },
    { label: "Obsidian", sublabel: "connecté", dotClass: "obsidian", connected: true },
    { label: "GitHub", sublabel: "@TechnoSecur", dotClass: "", connected: true },
  ]);

  const toggleSection = (label: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Load project count for badge + channel statuses
  useEffect(() => {
    const loadData = async () => {
      try {
        const projects = await api.projects.list();
        setProjectCount(projects.length);
      } catch {
        // ignore
      }

      // Try to load channel statuses (CFG-05 via client unifié)
      try {
        const data = await api.health.channels();
        setChannels([
          { label: "Discord", sublabel: data.discord === "connected" ? "connecté" : "hors ligne", dotClass: "discord", connected: data.discord === "connected" },
          { label: "Obsidian", sublabel: data.obsidian === "connected" ? "connecté" : "hors ligne", dotClass: "obsidian", connected: data.obsidian === "connected" },
          { label: "GitHub", sublabel: data.github || "@TechnoSecur", dotClass: "", connected: !!data.github && data.github !== "disconnected" },
        ]);
      } catch {
        // fallback to static display
      }
    };
    loadData();

    // Refresh channels every 60s
    const interval = setInterval(loadData, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Build nav sections with dynamic badge for Projets
  const navSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.map((item) =>
      item.label === "Projets" && projectCount > 0
        ? { ...item, badge: String(projectCount) }
        : item,
    ),
  }));

  return (
    <div className="app">
      {/* ── Sidebar ───────────────────────────────────────────── */}
      <aside className="sidebar">
        <div className="brand">
          <div className="logo">
            <svg className="synapse-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="12,2 22,8.5 22,15.5 12,22 2,15.5 2,8.5" />
              <line x1="12" y1="22" x2="12" y2="15.5" />
              <polyline points="22,8.5 12,15.5 2,8.5" />
            </svg>
            Synapse
          </div>
          <div className="domain">ia.technosecur.com</div>
        </div>

        {navSections.map((section) => {
          const collapsed = collapsedSections.has(section.label);
          return (
            <div key={section.label} className={`nav-section ${collapsed ? "collapsed" : ""}`}>
              <div
                className="section-label"
                onClick={() => toggleSection(section.label)}
              >
                {section.label}
                <span className="section-caret">▼</span>
              </div>
              <div className="nav-section-items">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `nav-item ${isActive ? "active" : ""}`
                    }
                  >
                    <span className="icon">{item.icon}</span>
                    {item.label}
                    {item.badge && <span className="badge">{item.badge}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}

        <div className="comms-center">
          <div className="comms-label" style={{
            fontFamily: "var(--mono)",
            fontSize: "9px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "var(--muted)",
            marginBottom: "2px",
          }}>Canaux</div>
          {channels.map((ch) => (
            <div key={ch.label} className="crow">
              <span className={`dot ${ch.dotClass}`} style={!ch.connected ? { background: "var(--rust)" } : undefined} />
              {ch.label} — {ch.sublabel}
            </div>
          ))}
        </div>
      </aside>

      {/* ── Main Panel ────────────────────────────────────────── */}
      <main className="main-panel">
        {/* ── Header bar: search + sync + notifications + profile ── */}
        <div className="header-bar">
          <GlobalSearch />
          <div className="header-actions-bar">
            <SyncButton />
            <NotificationsBell />
            <UserProfile />
          </div>
        </div>

        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/kanban" element={<Dashboard />} />
          <Route path="/new-idea" element={<Dashboard />} />
          <Route path="/mission/:id" element={<MissionControl />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/crew" element={<Crew />} />
          <Route path="/logs" element={<Logs />} />
          <Route path="/cicd" element={<Cicd />} />
          <Route path="/cluster" element={<Cluster />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/schedules" element={<Schedules />} />
          <Route path="/drp" element={<DRP />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </main>

      {/* Global toast notifications */}
      <ToastContainer />
    </div>
  );
}
